import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { IMyPermissions, PermissionAction } from '../interfaces/account';
import { ApiService } from './api.service';

const ACTION_FIELD = {
  create: 'allowCreate',
  read: 'allowRead',
  update: 'allowUpdate',
  delete: 'allowDelete',
  print: 'allowPrint',
  export: 'allowExport',
  import: 'allowImport',
} as const;

/**
 * Quyền của tài khoản đang đăng nhập.
 * Chỉ dùng để ẩn/hiện giao diện; BE vẫn kiểm tra quyền trên từng API.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private http = inject(ApiService);

  private state = signal<IMyPermissions | null>(null);
  private loadedForToken: string | null = null;
  private pending$: Observable<IMyPermissions> | null = null;

  readonly permissions = this.state.asReadonly();
  readonly isSuperAdmin = computed(() => this.state()?.isSuperAdmin ?? false);

  /** Tải quyền một lần cho mỗi token; đổi tài khoản thì tự tải lại */
  load(force = false): Observable<IMyPermissions> {
    const token = localStorage.getItem('auth_token');
    const current = this.state();
    if (!force && current && this.loadedForToken === token) return of(current);

    if (!this.pending$) {
      this.pending$ = this.http.get<IMyPermissions>('roles/me').pipe(
        tap((data) => {
          this.state.set(data);
          this.loadedForToken = token;
        }),
        finalize(() => (this.pending$ = null)),
        shareReplay(1),
      );
    }
    return this.pending$;
  }

  can(functionCode: string, action: PermissionAction = 'read'): boolean {
    const permission = this.state()?.permissions.find((p) => p.code === functionCode);
    return !!permission?.[ACTION_FIELD[action]];
  }

  clear(): void {
    this.state.set(null);
    this.loadedForToken = null;
  }
}
